/**
 * GoalCapacityPanel — the live capacity tile, on the Command Center home.
 *
 * Players online right now, tonight's peak, and the capacity this game has
 * actually been tested to, split between the people in a bar and the people at
 * home. It draws NOTHING unless the viewer is a super admin and the
 * organization in scope is running Tap'N Win — a Command Center for any other
 * vertical never renders it and never asks for it.
 *
 * The numbers come from counts the backend already keeps in memory beside the
 * polls that were happening anyway: no database write per phone, and reading
 * this costs one small request every ten seconds.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Home, Store } from 'lucide-react';
import { useAuth } from '../../../lib/auth';
import api from '../../../lib/api';

const EVERY = 10000;

const Stat = ({ label, value, testId, tone = '' }) => (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
        <p className="text-xs uppercase tracking-wide opacity-60">{label}</p>
        <p className={`text-2xl font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</p>
    </div>
);

export default function GoalCapacityPanel({ scopedOrgId, data }) {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const orgId = scopedOrgId || data?.organization_id || '';
    const [live, setLive] = useState(null);
    const [draft, setDraft] = useState('');
    const [saved, setSaved] = useState(false);
    const allowed = isSuperAdmin() && !!orgId;

    const read = useCallback(async () => {
        try {
            const r = await api.get('/goal-game/admin/capacity',
                                    { params: { organization_id: orgId } });
            setLive(r.data || { enabled: false });
            setDraft((d) => (d === '' && r.data?.tested_capacity
                ? String(r.data.tested_capacity) : d));
        } catch (_e) {
            setLive({ enabled: false });
        }
    }, [orgId]);

    useEffect(() => {
        if (!allowed) return undefined;
        read();
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') read();
        }, EVERY);
        return () => clearInterval(id);
    }, [allowed, read]);

    // Not a super admin, or not a Tap'N Win organization: nothing is drawn, so
    // there is no tile and no empty gap on any other Command Center.
    if (!allowed || !live || !live.enabled) return null;

    const save = async () => {
        await api.put('/goal-game/admin/capacity', {
            organization_id: orgId, tested_capacity: Number(draft) || 1,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        read();
    };

    const over = live.online > (live.tested_capacity || 0);

    return (
        <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4"
                 data-testid="cc-goal-capacity-panel">
            <div className="flex items-center gap-2">
                <Activity size={18} className="text-emerald-400" />
                <h3 className="text-lg font-bold">{t('goalGame.capacityTitle')}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Stat label={t('goalGame.capacityNow')} value={live.online}
                      testId="capacity-online-now"
                      tone={over ? 'text-amber-400' : 'text-emerald-400'} />
                <Stat label={t('goalGame.capacityPeak')} value={live.peak_tonight}
                      testId="capacity-peak-tonight" />
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                    <p className="text-xs uppercase tracking-wide opacity-60">
                        {t('goalGame.capacityTested')}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <input type="number" min="1" value={draft}
                               onChange={(e) => setDraft(e.target.value)}
                               data-testid="capacity-tested-input"
                               className="w-24 rounded-lg bg-white/10 border border-white/15 px-2 py-1 text-lg font-bold tabular-nums outline-none" />
                        <button onClick={save} data-testid="capacity-tested-save"
                                className="rounded-lg border border-white/20 px-3 py-1.5 text-sm font-semibold hover:bg-white/10 transition-colors">
                            {saved ? t('goalGame.capacitySaved') : t('goalGame.capacitySave')}
                        </button>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm" data-testid="capacity-at-venue">
                    <Store size={15} className="opacity-60" />
                    <span className="opacity-70">{t('goalGame.capacityAtVenue')}</span>
                    <span className="font-bold tabular-nums">{live.at_venue}</span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-testid="capacity-at-home">
                    <Home size={15} className="opacity-60" />
                    <span className="opacity-70">{t('goalGame.capacityAtHome')}</span>
                    <span className="font-bold tabular-nums">{live.at_home}</span>
                </div>
            </div>
            <p className="text-xs opacity-50">{t('goalGame.capacityNote')}</p>
        </section>
    );
}
